import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/Field";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const HOLIDAY_TEXT = "Grazie per il tuo messaggio! Lo studio è chiuso per ferie fino a {fine}: la Dott.ssa Simona Marretta ti risponderà {quando}.";

const WeekHours = ({ week, onChange }) => (
  <div className="space-y-2">
    {week.map((d, i) => {
      const set = (patch) => onChange(week.map((x, j) => (j === i ? { ...x, ...patch } : x)));
      return (
        <div key={i} className="grid grid-cols-[110px_auto_1fr_1fr] items-center gap-3" data-testid={`auto-day-${i}`}>
          <span className="text-sm font-semibold">{DAYS[i]}</span>
          <Switch checked={d.open} onCheckedChange={(v) => set({ open: v })} data-testid={`auto-day-open-${i}`} />
          {d.open ? (
            <>
              <input type="time" className="field" value={d.start} onChange={(e) => set({ start: e.target.value })} data-testid={`auto-day-start-${i}`} />
              <input type="time" className="field" value={d.end} onChange={(e) => set({ end: e.target.value })} data-testid={`auto-day-end-${i}`} />
            </>
          ) : <span className="col-span-2 text-sm text-slate-400">Chiuso</span>}
        </div>
      );
    })}
  </div>
);

const Holidays = ({ items, onChange }) => (
  <div className="space-y-3">
    {items.map((h, i) => {
      const set = (patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
      return (
        <div key={i} className="space-y-2 rounded-xl border border-[#e2e8f0] p-3" data-testid={`auto-holiday-${i}`}>
          <div className="flex items-end gap-2">
            <Field label="Dal"><input type="date" className="field" value={h.start} onChange={(e) => set({ start: e.target.value })} data-testid={`auto-holiday-start-${i}`} /></Field>
            <Field label="Al"><input type="date" className="field" value={h.end} onChange={(e) => set({ end: e.target.value })} data-testid={`auto-holiday-end-${i}`} /></Field>
            <button type="button" className="btn-ghost mb-1 text-slate-400 hover:text-rose-600" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`auto-holiday-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <textarea className="field min-h-[60px]" value={h.text} onChange={(e) => set({ text: e.target.value })} data-testid={`auto-holiday-text-${i}`} />
        </div>
      );
    })}
    <button type="button" className="btn-outline" onClick={() => onChange([...items, { start: "", end: "", text: HOLIDAY_TEXT }])} data-testid="auto-holiday-add"><Plus className="h-4 w-4" /> Aggiungi ferie</button>
  </div>
);

export function AutoReplyDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) api.get("/settings/auto-reply").then((r) => setCfg(r.data));
  }, [open]);
  const save = async () => {
    if (cfg.holidays.some((h) => !h.start || !h.end)) return toast.error("Inserisci le date di tutte le ferie");
    setSaving(true);
    try {
      const { preview, ...body } = cfg;
      const { data } = await api.put("/settings/auto-reply", body);
      setCfg(data);
      toast.success("Risposta automatica salvata");
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="auto-reply-dialog">
        <DialogHeader><DialogTitle>Risposta automatica</DialogTitle></DialogHeader>
        {cfg && (
          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-xl bg-[#edf2ef] px-4 py-3">
              <span className="text-sm font-medium text-[#2f4f47]">Invia la risposta automatica fuori orario</span>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="auto-reply-enabled" />
            </div>
            <div className="space-y-2">
              <div className="label-xs">Orari dello studio</div>
              <WeekHours week={cfg.week} onChange={(week) => setCfg({ ...cfg, week })} />
            </div>
            <Field label="Testo fuori orario">
              <textarea className="field min-h-[80px]" value={cfg.text} onChange={(e) => setCfg({ ...cfg, text: e.target.value })} data-testid="auto-reply-text" />
            </Field>
            <p className="-mt-4 text-xs text-slate-500">{"{quando}"} diventa "oggi", "domani" o il giorno di riapertura. Nelle ferie {"{fine}"} diventa l'ultimo giorno di ferie.</p>
            <div className="space-y-2">
              <div className="label-xs">Ferie</div>
              <Holidays items={cfg.holidays} onChange={(holidays) => setCfg({ ...cfg, holidays })} />
            </div>
            <div className="rounded-xl bg-[#fbf8f4] p-3 text-sm text-[#8a6d4f]" data-testid="auto-reply-preview">
              <span className="font-semibold">Adesso: </span>{cfg.preview || "nessuna risposta automatica (studio aperto o funzione disattivata)"}
            </div>
            <div className="flex justify-end"><button className="btn-primary" disabled={saving} onClick={save} data-testid="auto-reply-save">Salva</button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
